import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useToggle, useGeolocation } from "react-use";
import { isMobile } from "react-device-detect";
import { CircularProgress, Grid, TextareaAutosize, makeStyles } from "@material-ui/core";
import CameraAltOutlinedIcon from "@material-ui/icons/CameraAltOutlined";
import AddAPhotoOutlinedIcon from "@material-ui/icons/AddAPhotoOutlined";
import CheckOutlinedIcon from "@material-ui/icons/CheckOutlined";
import ReplayIcon from "@material-ui/icons/Replay";
import { RoundedButton, Typography } from "@packages/ui";
import WarningScreen from "./WarningScreen";
import CompulsoryImageButton from "./CompulsoryImageButton";
import { uploadPhotosToWecapture, buildMetadata } from "../../utils/uploadPhotos";
import * as appActions from "../../actions/appActions";
import { useCurrentRental } from "../../hooks/rentalHooks";
import { Alert } from '@material-ui/lab';

const useStyles = makeStyles((theme) => ({
  input: {
    display: "none",
  },
  root: {
    width: '100%',
    '& > * + *': {
      marginTop: theme.spacing(2),
    },
    padding: '0px 0px 0px 0px'
  },
  message:{
    padding: '0px 0px 0px 0px',
    marginBottom:'20px'
  },
  WarningIcon: {
    "& .MuiAlert-icon": {
      color:'red'
    }
  }
}));

const UploadRentalPhoto = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const navigate = useNavigate();
  const auth = useSelector((state) => ({ ...state.auth }));
  const currentRental = useCurrentRental();
  const { latitude, longitude } = useGeolocation({ enableHighAccuracy: true });
  const [showWarning, toggleShowWarning] = useToggle(true);
  const extraFilesLimit = 5;
  const [dashboard, setDashboard] = useState(false);
  const [front, setFront] = useState(false);
  const [back, setBack] = useState(false);
  const [left, setLeft] = useState(false);
  const [right, setRight] = useState(false);
  const [extraPhotos, setExtraPhotos] = useState([]);
  const [uploading, toggleUploading] = useToggle(false);
  const reachedMaxExtraPhotos = extraPhotos.length === extraFilesLimit;
  const disableExtraPhotoButton = uploading || reachedMaxExtraPhotos;
  const extraPhotosIndexes = ["1", "2", "3", "4", "5"];
  const [comments, setComments] = useState("");
  const changeComments = (event) => setComments(event.target.value);

  const uploadFiles = async () => {
    const photos = [dashboard, front, back, left, right, ...extraPhotos];
    if (!dashboard || !front || !back || !left || !right) {
      const missingImage = dashboard
        ? front
          ? back
            ? left
              ? "driver side"
              : "passenger side"
            : "rear of vehicle"
          : "front of vehicle"
        : "dashboard of vehicle";
      dispatch(
        appActions.createToast({
          type: "error",
          message: `Please ensure ${missingImage} photo is provided`,
        })
      );
      return;
    }
/* for testing */
    // if (!latitude || !longitude) {
    //   dispatch(
    //     appActions.createToast({
    //       type: "error",
    //       message:
    //         "There is an error getting your location coordinates. Photos can not be uploaded. Please check your browser settings and make sure location services are enabled to proceed.",
    //     })
    //   );
    //   return;
    // }

    toggleUploading(true);

    // start uploading process
    const metadata = buildMetadata({
      vehicleNumber: currentRental.vehicle.vehicle_number,
      raNumber: currentRental.rental_agreement_number,
      latitude,
      longitude,
      country: currentRental.country || "AU",
      pickUp: currentRental.pickup.dateTime,
      dropOff: currentRental.dropoff.dateTime,
    });
    const uploadResults = await uploadPhotosToWecapture(photos, metadata);
    const failedUpload = uploadResults.filter((response) => response.success === 0);
    if (failedUpload.length > 0) {
      dispatch(
        appActions.createToast({
          type: "error",
          message: "There is an error uploading your images. Please try again later.",
        })
      );
      // Finish uploading process
      toggleUploading(false);
      navigate("/app/rental-photos");
      return;
    }
    
    dispatch(
      appActions.createToast({
        type: "success",
        message: "All images are uploaded.",
      })
    );

    // trigger Saga to update pictures in the backend
    const { batch: batchId, type: imageType } = metadata;
    dispatch(
      appActions.updatePhotos({
        ...auth,
        batchId,
        imageType,
        comments,
        raNumber: currentRental.rental_agreement_number,
      })
    );

    // Finish uploading process
    toggleUploading(false);
    navigate("/app/rental-photos");
  };

  const incOnePhoto = () => {
    const id = "extra-photo-" + (extraPhotos.length + 1);
    document.getElementById(id).click();
  };

  const updatePhoto = (id) => {
    document.getElementById(id).click();
  };

  const resetExtraPhoto = () => {
    setExtraPhotos([]);
    extraPhotosIndexes.forEach((indexNo) => {
      const id = `extra-photo-${indexNo}`;
      document.getElementById(id).value = "";
    });
  };

  const additionalPhoto = (files, index) => {
    if (extraPhotos[index]) {
      const newArray = extraPhotos;
      newArray[index] = files[files.length - 1];
      setExtraPhotos([...newArray]);
    } else {
      setExtraPhotos([...extraPhotos, files[0]]);
    }
  };

  return (
    <>
      {showWarning && <WarningScreen onExit={toggleShowWarning} />}
      {!showWarning && (
        <>
          <Grid container direction="row" alignItems="center">
            <Grid
              container
              alignItems="center"
              style={{ maxWidth: 650, marginTop: 0, marginBottom: 20 }}
            >
              <Typography paragraph variant="h4">
                Compulsory Photos
              </Typography>
              <Grid 
              alignItems="center"
              container>
              <div className={classes.root}> 
              <Alert severity="warning" style={{color:'black', backgroundColor:'white', fill:'red'}} className={classes.message+' '+classes.WarningIcon}>
              
              <i><strong> Please check surroundings for safety.</strong></i>
             
              </Alert>
              </div>
              </Grid>
              <Grid container alignItems="center" style={{ textAlign: "center" }}>
                <Grid item xs={12}>
                <Typography paragraph variant="subtitle2">
                  Click on each camera icon to take photos
                </Typography>
                  <label htmlFor="left-photo">
                    <CompulsoryImageButton disabled={uploading}>
                      {left ? <CheckOutlinedIcon /> : <CameraAltOutlinedIcon />}
                    </CompulsoryImageButton>
                  </label>
                </Grid>
              </Grid>
              <Grid container direction="row" justify="center" alignItems="center">
                <label htmlFor="back-photo">
                  <CompulsoryImageButton disabled={uploading}>
                    {back ? <CheckOutlinedIcon /> : <CameraAltOutlinedIcon />}
                  </CompulsoryImageButton>
                </label>
                <img
                  src="https://s3-ap-southeast-2.amazonaws.com/static.hertz.asiapac.io-prod/hertz-style-guide/images/illustration-car-top-down.png"
                  alt="Outline of a car"
                  style={{ maxWidth: 170, margin: 5 }}
                />

                <label htmlFor="front-photo">
                  <CompulsoryImageButton disabled={uploading}>
                    {front ? <CheckOutlinedIcon /> : <CameraAltOutlinedIcon />}
                  </CompulsoryImageButton>
                </label>
              </Grid>
              <Grid item container>
                <Grid item xs={12} style={{ textAlign: "center" }}>
                  <label htmlFor="right-photo">
                    <CompulsoryImageButton disabled={uploading}>
                      {right ? <CheckOutlinedIcon /> : <CameraAltOutlinedIcon />}
                    </CompulsoryImageButton>
                  </label>
                </Grid>
              </Grid>
              <Grid item xs={12} style={{ marginTop: 20, marginBottom: 20, textAlign: "center" }}>

                <label htmlFor="dashboard-photo">
                  <CompulsoryImageButton disabled={uploading}>
                    Dashboard Photo{" "}
                    {dashboard ? (
                      <CheckOutlinedIcon style={{ marginLeft: 3 }} />
                    ) : (
                      <CameraAltOutlinedIcon style={{ marginLeft: 3 }} />
                    )}
                  </CompulsoryImageButton>
                </label>
                <Typography paragraph variant="subtitle2" style={{ marginTop: 5, marginBottom: 10, fontSize:'10px'}}>
                  Ensure current km reading can be seen
              </Typography>

              </Grid>
            </Grid>
            {/* map out compulsory photo inputs */}
            {[
              { function: setDashboard, label: "dashboard-photo" },
              { function: setFront, label: "front-photo" },
              { function: setBack, label: "back-photo" },
              { function: setLeft, label: "left-photo" },
              { function: setRight, label: "right-photo" },
            ].map((value, index) => {
              return (
                <input
                  accept="image/*"
                  className={classes.input}
                  id={value.label}
                  type="file"
                  onChange={(event) => {
                    value.function(event.target.files[0]);
                  }}
                  key={index}
                />
              );
            })}
            <Grid
              item
              container
              xs={12}
              alignItems="center"
              style={{ maxWidth: 650, minWidth: 20, minHeight: 40, marginBottom: 20 }}
            >
              <Typography paragraph variant="h4">
                Additional Photo(s)
              </Typography>
              <Grid container item xs={12} alignItems="center">
                <Grid item xs={12} style={{ textAlign: "center" }}>
                  <RoundedButton
                    style={{ marginRight: 10, marginBottom: 10 }}
                    disabled={disableExtraPhotoButton}
                    onClick={incOnePhoto}
                  >
                    <AddAPhotoOutlinedIcon style={{ marginRight: 10 }} />
                    Additional Photo <br/>(optional)
                  </RoundedButton>
                  {extraPhotos.length > 0 && (
                    <RoundedButton onClick={resetExtraPhoto} style={{ marginBottom: 10 }}>
                      <ReplayIcon />
                    </RoundedButton>
                  )}
                </Grid>
              </Grid>
              {extraPhotos.length > 0 &&
                extraPhotos.map((value, index) => {
                  const id = `extra-photo-${index + 1}`;
                  const label = `${value.name}`;
                  return (
                    <Grid
                      container
                      item
                      sm={12}
                      key={id}
                      style={{ marginBottom: 10 }}
                      alignItems="center"
                    >
                      <input accept="image/*" className={classes.input} type="file" />
                      <Grid item xs={12} style={{ textAlign: "center" }}>
                        <RoundedButton
                          title={label}
                          onClick={() => updatePhoto(id)}
                          variant="contained"
                          component="span"
                          disabled={uploading}
                        />
                      </Grid>
                    </Grid>
                  );
                })}
            </Grid>
            {/* map out extra photo inputs */}
            {extraPhotosIndexes.map((value, index) => {
              return (
                <input
                  accept="image/*"
                  className={classes.input}
                  id={"extra-photo-" + value}
                  onChange={(e) => additionalPhoto(e.target.files, index)}
                  type="file"
                  key={index}
                />
              );
            })}
          </Grid>
          <Grid item md={12} style={{ marginTop: 15 }}>
            <Typography paragraph variant="h4">
              Add comments (optional)
            </Typography>
            <Typography paragraph variant="subtitle2">
              <i>If you have any further comments, please let us know.</i>
            </Typography>
            <TextareaAutosize
              aria-label="comments"
              value={comments}
              onChange={changeComments}
              rowsMin={4}
              style={isMobile ? { width: "100%" } : { width: "60%" }}
            />
          </Grid>
          <Grid container item xs={12} alignItems="center" style={{ maxWidth: 650 }}>
            <Grid item xs={12} style={{ textAlign: "center" }}>
              <RoundedButton style={{ marginTop: 10 }} onClick={uploadFiles} disabled={uploading}>
                Submit Photos
                {uploading && <CircularProgress />}
              </RoundedButton>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
};

export default UploadRentalPhoto;
